import React from 'react';
import { Box, Typography, Paper, Grid, Alert, Divider, List, ListItem, ListItemIcon, ListItemText, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon,
  LooksOne as Step1Icon,
  LooksTwo as Step2Icon,
  Looks3 as Step3Icon,
  Looks4 as Step4Icon,
  Looks5 as Step5Icon,
  Shuffle as ShuffleIcon,
  EmojiEvents as WinnerIcon,
  AccountBalanceWallet as PaymentIcon,
} from '@mui/icons-material';
import { SupportLayout, InfoCard, VisualExample, StepIndicator } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';

export const GettingStartedPage: React.FC = () => {
  useDocumentTitle('Getting Started - Support');
  const navigate = useNavigate();

  const overviewSteps = [
    {
      title: 'Create a Board',
      description: 'Set up your sports squares board with teams, game date, and payout configuration.',
    },
    {
      title: 'Invite Players',
      description: 'Share your invite code so players can join and claim squares.',
    },
    {
      title: 'Randomize Numbers',
      description: 'Once all squares are claimed, randomize the 0-9 numbers for each team.',
    },
    {
      title: 'Lock the Board',
      description: 'Lock the board before the game starts to prevent changes.',
    },
    {
      title: 'Record Scores',
      description: 'Enter scores after each quarter to determine winners.',
    },
  ];

  return (
    <SupportLayout
      title="Getting Started with SquareUp"
      description="Learn how to create and manage your first sports squares board in just a few minutes."
      breadcrumbs={[{ label: 'Getting Started' }]}
    >
      {/* What is a squares pool */}
      <Paper elevation={0} sx={{ p: 4, mb: 4, bgcolor: 'primary.50', border: 1, borderColor: 'primary.200' }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          What is a Squares Pool?
        </Typography>
        <Typography variant="body1" paragraph>
          A squares pool (also called "football squares" or "Super Bowl squares") is a popular game where players purchase squares on a grid. Each square corresponds to the last digit of each team's score at the end of a quarter or the game.
        </Typography>
        <Typography variant="body1">
          For example, if the score is <strong>Chiefs 17, Eagles 14</strong>, the winning square is where row <strong>4</strong> (Eagles' last digit) meets column <strong>7</strong> (Chiefs' last digit).
        </Typography>
      </Paper>

      {/* Quick overview */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          How It Works (5 Simple Steps)
        </Typography>
        <StepIndicator steps={overviewSteps} />
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Step 1: Create a Board */}
      <Box id="step-1" sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Step1Icon />
          </Box>
          <Typography variant="h5" fontWeight="bold">
            Step 1: Create Your Board
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          Click <strong>"Create Board"</strong> from the navigation menu or dashboard. You'll set up the following:
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Basic Information</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Board Name" secondary="e.g., 'Super Bowl 2025 Pool'" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Sport" secondary="Football, Basketball, Hockey, etc." />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Teams" secondary="Home and Away team names" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Game Date & Time" secondary="When the game takes place" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Pricing & Payouts</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Price Per Square" secondary="How much each square costs" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Number of Quarters" secondary="2, 3, or 4 scoring periods" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Payout Configuration" secondary="How winnings are distributed" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Optional: Theme" secondary="Customize the board's appearance" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mt: 3 }}>
          <strong>Tip:</strong> You can save your configuration as a template to quickly create similar boards in the future.
        </Alert>
      </Box>

      {/* Step 2: Invite Players */}
      <Box id="step-2" sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Step2Icon />
          </Box>
          <Typography variant="h5" fontWeight="bold">
            Step 2: Invite Players
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          Once your board is created, you'll receive an <strong>invite code</strong>. Share this code with friends and family so they can join your pool.
        </Typography>

        <VisualExample title="Invite Code Example" caption="Players enter this code to join your board">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Your Invite Code
            </Typography>
            <Paper
              sx={{
                display: 'inline-block',
                px: 4,
                py: 2,
                bgcolor: 'grey.100',
                borderRadius: 2,
              }}
            >
              <Typography variant="h4" fontFamily="monospace" fontWeight="bold" letterSpacing={4}>
                ABC123
              </Typography>
            </Paper>
          </Box>
        </VisualExample>

        <Typography variant="body1" paragraph>
          Players can join by:
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText primary="Clicking 'Join Board' in their navigation" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText primary="Entering your invite code" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText primary="Selecting which squares they want to claim" />
          </ListItem>
        </List>

        <InfoCard title="Offline Players" variant="tip">
          <Typography variant="body2">
            Have players who don't want to create an account? You can add <strong>offline players</strong> and assign squares to them manually. This is perfect for family members or friends who prefer the old-school approach.
          </Typography>
        </InfoCard>
      </Box>

      {/* Step 3: Randomize Numbers */}
      <Box id="step-3" sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Step3Icon />
          </Box>
          <Typography variant="h5" fontWeight="bold">
            Step 3: Randomize the Numbers
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          Once all squares have been claimed (or you're ready to start), you'll randomize the numbers. This assigns random digits 0-9 to both the rows (one team) and columns (other team).
        </Typography>

        <VisualExample title="Number Randomization" caption="Each team gets a random arrangement of 0-9">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="primary.main" fontWeight="bold" sx={{ mb: 1 }}>
                Before Randomization
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.200' }}>
                <Typography fontFamily="monospace">Home: ?, ?, ?, ?, ?, ?, ?, ?, ?, ?</Typography>
                <Typography fontFamily="monospace">Away: ?, ?, ?, ?, ?, ?, ?, ?, ?, ?</Typography>
              </Paper>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ShuffleIcon fontSize="large" color="action" />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="success.main" fontWeight="bold" sx={{ mb: 1 }}>
                After Randomization
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'success.50' }}>
                <Typography fontFamily="monospace">Home: 3, 7, 1, 9, 0, 5, 2, 8, 4, 6</Typography>
                <Typography fontFamily="monospace">Away: 8, 2, 5, 0, 7, 4, 9, 1, 6, 3</Typography>
              </Paper>
            </Box>
          </Box>
        </VisualExample>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <strong>Important:</strong> Numbers are hidden until you choose to reveal them. Many organizers wait until the game starts to reveal numbers for added excitement!
        </Alert>
      </Box>

      {/* Step 4: Lock the Board */}
      <Box id="step-4" sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Step4Icon />
          </Box>
          <Typography variant="h5" fontWeight="bold">
            Step 4: Lock the Board
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          Before the game starts, <strong>lock your board</strong>. This prevents any changes to square ownership and ensures fair play.
        </Typography>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>Board Status Flow</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
            {[
              { label: 'Draft', color: 'grey.500', desc: 'Setting up' },
              { label: 'Live', color: 'success.main', desc: 'Accepting players' },
              { label: 'Locked', color: 'warning.main', desc: 'Game in progress' },
              { label: 'Completed', color: 'info.main', desc: 'All scores recorded' },
            ].map((status, index) => (
              <React.Fragment key={status.label}>
                <Paper
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: status.color,
                    color: 'white',
                    borderRadius: 1,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">{status.label}</Typography>
                  <Typography variant="caption">{status.desc}</Typography>
                </Paper>
                {index < 3 && <ArrowIcon color="action" />}
              </React.Fragment>
            ))}
          </Box>
        </Paper>
      </Box>

      {/* Step 5: Record Scores */}
      <Box id="step-5" sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Step5Icon />
          </Box>
          <Typography variant="h5" fontWeight="bold">
            Step 5: Record Scores & Celebrate Winners
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          After each quarter, enter the current score. SquareUp automatically:
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { icon: <CheckIcon />, text: 'Identifies the winning square' },
            { icon: <WinnerIcon />, text: 'Highlights the winner on the board' },
            { icon: <PaymentIcon />, text: 'Calculates and records the payout' },
          ].map((item, index) => (
            <Grid size={{ xs: 12, sm: 4 }} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 1 }}>{item.icon}</Box>
                <Typography variant="body2">{item.text}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <VisualExample title="Score Entry Example" caption="Enter scores after each quarter to determine winners">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">Q1 Score</Typography>
              <Typography variant="h5" fontWeight="bold">Chiefs 7, Eagles 3</Typography>
            </Box>
            <ArrowIcon fontSize="large" color="action" />
            <Paper sx={{ p: 2, bgcolor: 'success.50', textAlign: 'center' }}>
              <Typography variant="subtitle2" color="success.main" fontWeight="bold">Winner!</Typography>
              <Typography variant="body2">Row 3 x Column 7</Typography>
              <Typography variant="body2" fontWeight="bold">Payout: $100</Typography>
            </Paper>
          </Box>
        </VisualExample>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Next steps */}
      <Box>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          What's Next?
        </Typography>
        <Grid container spacing={2}>
          {[
            { label: 'Learn about payout options', path: '/support/payouts', color: 'warning.main' },
            { label: 'Understand game mechanics', path: '/support/game-mechanics', color: 'secondary.main' },
            { label: 'Set up accounting', path: '/support/accounting', color: 'error.main' },
          ].map((item) => (
            <Grid size={{ xs: 12, sm: 4 }} key={item.path}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate(item.path)}
                sx={{ py: 2, justifyContent: 'space-between' }}
              >
                {item.label}
                <ArrowIcon />
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    </SupportLayout>
  );
};

export default GettingStartedPage;
